import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, FileDown, RefreshCw, Clock, AlertTriangle, CalendarClock, Layers } from "lucide-react";
import api from "../lib/api";

const ageTone = (d) => (d >= 14 ? "bg-red-100 text-red-700 border-red-200" : d >= 7 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-600 border-slate-200");

export default function FederalPipeline() {
  const [data, setData] = useState(null);
  const [showEmpty, setShowEmpty] = useState(false);

  const load = () => api.get("/admin/federal/pipeline").then((r) => setData(r.data)).catch(() => toast.error("Failed to load federal pipeline"));
  useEffect(() => { load(); }, []);

  const downloadPdf = async () => {
    try {
      const res = await api.get("/admin/federal/pipeline.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "federal-pipeline.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  if (!data) return <div className="text-slate-400 text-[14px]" data-testid="federal-pipeline-loading">Loading federal pipeline…</div>;
  const t = data.totals;
  const cards = [
    { label: "Open Requirements", value: t.open, icon: Layers, testid: "fp-total-open" },
    { label: "In Stage 7–13 Days", value: t.aging_7, icon: Clock, testid: "fp-aging-7", tone: t.aging_7 ? "text-amber-700" : "" },
    { label: "In Stage 14+ Days", value: t.aging_14, icon: AlertTriangle, testid: "fp-aging-14", tone: t.aging_14 ? "text-red-700" : "" },
    { label: "Response Due ≤ 7 Days", value: t.deadline_7, icon: CalendarClock, testid: "fp-deadline-7", tone: t.deadline_7 ? "text-amber-700" : "" },
  ];
  const stages = data.stages.filter((s) => showEmpty || s.count > 0);

  return (
    <div data-testid="admin-federal-pipeline-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <ShieldCheck size={22} className="text-[var(--pg-accent)]" />
          <div>
            <h1 className="font-[Poppins] font-bold text-[22px] text-[var(--pg-navy)]">Federal Pipeline</h1>
            <p className="text-[13px] text-slate-500">Every open Federal / DLA and Prime Contractor requirement by lifecycle stage · avg {t.avg_days} days in stage</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="fp-refresh" onClick={load} className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-white border border-slate-200 text-slate-600 hover:border-[var(--pg-accent)] hover:text-[var(--pg-navy)]"><RefreshCw size={14} /> Refresh</button>
          <button data-testid="fp-download-pdf" onClick={downloadPdf} className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)]"><FileDown size={14} /> Export PDF</button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 text-[11.5px] uppercase tracking-wider text-slate-500 font-[Poppins] font-semibold"><c.icon size={14} /> {c.label}</div>
            <div className={`mt-1.5 font-[Poppins] font-extrabold text-[26px] leading-none ${c.tone || "text-[var(--pg-navy)]"}`}>{c.value}</div>
          </div>
        ))}
      </div>

      <StageStrip stages={data.stages} />

      <label className="mt-5 inline-flex items-center gap-2 text-[12.5px] text-slate-500 cursor-pointer">
        <input type="checkbox" data-testid="fp-show-empty" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} className="accent-[var(--pg-accent)]" /> Show empty stages
      </label>

      {t.open === 0 && <p data-testid="fp-empty-note" className="mt-4 text-[13px] text-slate-500 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">No open federal requirements right now. New Federal / DLA or Prime Contractor RFQs will appear here automatically.</p>}

      <div className="mt-3 space-y-4">
        {stages.map((s) => <StageGroup key={s.key} stage={s} index={data.stages.indexOf(s) + 1} />)}
      </div>
    </div>
  );
}

function StageStrip({ stages }) {
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <div className="mt-5 bg-white rounded-xl border border-slate-200 p-4 overflow-x-auto" data-testid="fp-stage-strip">
      <div className="flex items-end gap-1 min-w-[900px]">
        {stages.map((s, i) => (
          <div key={s.key} className="flex-1 flex flex-col items-center gap-1" title={`${s.label}: ${s.count}`} data-testid={`fp-strip-${s.key}`}>
            <span className={`text-[11px] font-[Poppins] font-bold ${s.count ? "text-[var(--pg-navy)]" : "text-slate-300"}`}>{s.count}</span>
            <div className="w-full h-14 flex items-end"><div className={`w-full rounded-t ${s.count ? "bg-[var(--pg-accent)]" : "bg-slate-100"}`} style={{ height: `${Math.max(s.count ? 8 : 3, (s.count / max) * 100)}%` }} /></div>
            <span className="text-[9px] text-slate-400 font-[Poppins] text-center leading-tight">{i + 1}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StageGroup({ stage, index }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`fp-stage-${stage.key}`}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
        <div className="font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)]"><span className="text-slate-400 mr-2">{index}.</span>{stage.label}</div>
        <span data-testid={`fp-stage-count-${stage.key}`} className="text-[12px] font-[Poppins] font-semibold px-2 py-0.5 rounded-full bg-[var(--pg-navy)] text-white">{stage.count}</span>
      </div>
      {stage.items.length === 0 ? (
        <div className="px-5 py-4 text-[13px] text-slate-400">No requirements in this stage.</div>
      ) : (
        <table className="w-full text-left text-[13px]">
          <thead>
            <tr className="text-[10.5px] uppercase tracking-wider text-slate-500 font-[Poppins] border-b border-slate-100">
              <th className="px-5 py-2">RFQ #</th><th className="px-3 py-2">Customer / Agency</th><th className="px-3 py-2">Solicitation</th>
              <th className="px-3 py-2">Response Due</th><th className="px-3 py-2">Assigned</th><th className="px-5 py-2 text-right">Days in Stage</th>
            </tr>
          </thead>
          <tbody>
            {stage.items.map((r) => (
              <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`fp-row-${r.rfq_number}`}>
                <td className="px-5 py-2.5"><Link to={`/admin/rfqs/${r.id}`} data-testid={`fp-link-${r.rfq_number}`} className="font-[Poppins] font-semibold text-[var(--pg-accent)] hover:underline">{r.rfq_number}</Link>
                  <div className="text-[11px] text-slate-400">{r.program}</div></td>
                <td className="px-3 py-2.5"><div className="font-medium text-[var(--pg-navy)]">{r.company || r.customer}</div>{r.agency && r.agency !== r.company && <div className="text-[11.5px] text-slate-500">{r.agency}</div>}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.solicitation_number || "—"}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.response_deadline || "—"}
                  {r.deadline_days !== null && !r.response_submitted && r.deadline_days <= 7 && <span className={`ml-2 text-[10.5px] font-[Poppins] font-semibold px-1.5 py-0.5 rounded ${r.deadline_days < 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{r.deadline_days < 0 ? `${-r.deadline_days}d overdue` : r.deadline_days === 0 ? "due today" : `${r.deadline_days}d left`}</span>}
                  {r.response_submitted && <span className="ml-2 text-[10.5px] font-[Poppins] font-semibold px-1.5 py-0.5 rounded bg-green-100 text-green-700">submitted</span>}</td>
                <td className="px-3 py-2.5 text-slate-500">{r.assigned_to || "—"}</td>
                <td className="px-5 py-2.5 text-right"><span data-testid={`fp-days-${r.rfq_number}`} className={`inline-block min-w-[44px] text-center text-[12px] font-[Poppins] font-bold px-2 py-0.5 rounded-full border ${ageTone(r.days_in_stage)}`}>{r.days_in_stage}d</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
